import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import AppShell from "../components/AppShell";
import { Users, Crown, Check, X, LogOut, Trash2, MessageSquare, Mail, Ban } from "lucide-react";

const BUSINESS_ROLES = ["Founder", "Co-founder", "CEO", "CFO", "COO", "CTO", "Head of Product", "Board member", "Advisor", "Other"];

const NAV = [
  {to:"/founder/dashboard",label:"Dashboard"},
  {to:"/founder/tutorial",label:"Tutorial"},
  {to:"/founder/team",label:"Team"},
  {to:"/onboarding",label:"Onboarding"},
];

export default function FounderTeam() {
  const nav = useNavigate();
  const [team, setTeam] = useState([]);
  const [requests, setRequests] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [invites, setInvites] = useState([]);
  const [inviteForm, setInviteForm] = useState({ email: "", business_role: "Co-founder" });
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [t, r, o, iv] = await Promise.all([
      api.get("/companies/me/team"),
      api.get("/companies/me/join-requests").catch(() => ({ data: [] })),
      api.get("/companies/me/outgoing-requests").catch(() => ({ data: [] })),
      api.get("/companies/me/invites").catch(() => ({ data: [] })),
    ]);
    setTeam(t.data || []);
    setRequests((r.data || []).filter(x => x.status === "pending"));
    setOutgoing(o.data || []);
    setInvites(iv.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const me = team.find(m => m.is_me);
  const iAmPrimary = !!me?.is_primary;

  const approve = async (id) => {
    setBusy(true); setMsg("");
    try { await api.post(`/companies/me/join-requests/${id}/approve`); await load(); setMsg("Approved."); }
    catch (e) { setMsg(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const reject = async (id) => {
    setBusy(true); setMsg("");
    try { await api.post(`/companies/me/join-requests/${id}/reject`); await load(); setMsg("Rejected."); }
    catch (e) { setMsg(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const changeRole = async (uid, role) => {
    setBusy(true); setMsg("");
    try { await api.put(`/companies/me/members/${uid}/role`, { business_role: role }); await load(); }
    catch (e) { setMsg(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const removeMember = async (uid) => {
    if (!confirm("Remove this team member?")) return;
    setBusy(true); setMsg("");
    try { await api.delete(`/companies/me/members/${uid}`); await load(); }
    catch (e) { setMsg(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const sendInvite = async (e) => {
    e.preventDefault();
    setBusy(true); setMsg("");
    try {
      await api.post("/companies/me/invites", inviteForm);
      setInviteForm({ email: "", business_role: "Co-founder" });
      await load();
      setMsg("Invite sent — they'll get an email with a join link.");
    } catch (e2) { setMsg(e2.response?.data?.detail || "Failed to send invite"); }
    finally { setBusy(false); }
  };
  const revokeInvite = async (id) => {
    setBusy(true); setMsg("");
    try { await api.post(`/companies/me/invites/${id}/revoke`); await load(); }
    catch (e2) { setMsg(e2.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const leave = async () => {
    if (!confirm("Leave this business? You'll lose access to its dashboard.")) return;
    try { await api.post("/companies/me/leave"); nav("/onboarding"); }
    catch (e) { setMsg(e.response?.data?.detail || "Failed"); }
  };

  return (
    <AppShell nav={NAV}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-[#0A8B85]"/>
            <h1 className="text-3xl font-bold text-neutral-900">Team</h1>
          </div>
          <p className="mt-1 text-sm text-neutral-600">
            Everyone working on this business on Clinq. The primary founder can manage members and approve join requests.
          </p>
        </div>
        {me && !iAmPrimary && (
          <button onClick={leave} data-testid="founder-team-leave"
            className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-sm font-semibold text-neutral-700 hover:bg-neutral-50">
            <LogOut className="h-4 w-4"/> Leave business
          </button>
        )}
      </div>

      {msg && <div className="mt-4 rounded-lg border border-neutral-200 bg-white px-4 py-2 text-sm text-neutral-700" data-testid="team-msg">{msg}</div>}

      {/* Invite teammates (primary only) */}
      {iAmPrimary && (
        <div className="mt-6 rounded-2xl border border-[#0A8B85]/25 bg-[#0A8B85]/[0.04] p-6" data-testid="team-invite-card">
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-[#0A8B85]"/>
            <p className="text-xs font-bold uppercase tracking-wider text-neutral-600">Invite a teammate</p>
          </div>
          <p className="mt-1 text-sm text-neutral-600">
            Bring your co-founder, CFO or advisor in to collaborate on the capital raise — documents,
            score and funder matching, together.
          </p>
          <form onSubmit={sendInvite} className="mt-4 flex flex-wrap gap-2">
            <input type="email" required placeholder="teammate@company.com"
              value={inviteForm.email}
              onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
              data-testid="team-invite-email"
              className="min-w-[220px] flex-1 rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A8B85]" />
            <select value={inviteForm.business_role}
              onChange={(e) => setInviteForm({ ...inviteForm, business_role: e.target.value })}
              data-testid="team-invite-role"
              className="rounded-lg border border-neutral-200 bg-white px-2 py-2 text-sm font-semibold text-neutral-700 outline-none focus:border-[#0A8B85]">
              {BUSINESS_ROLES.filter(r => r !== "Founder").map(r => <option key={r}>{r}</option>)}
            </select>
            <button type="submit" disabled={busy} data-testid="team-invite-send"
              className="rounded-lg bg-[#0A8B85] px-4 py-2 text-sm font-bold text-white hover:bg-[#066667] disabled:opacity-50">
              Send invite
            </button>
          </form>
          {invites.length > 0 && (
            <ul className="mt-4 divide-y divide-neutral-100 rounded-xl border border-neutral-200 bg-white">
              {invites.map(iv => (
                <li key={iv.invite_id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5"
                    data-testid={`team-invite-${iv.invite_id}`}>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-neutral-900">{iv.email}</p>
                    <p className="text-xs text-neutral-500">{iv.business_role} · invited {new Date(iv.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={"rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase " +
                      (iv.status === "pending" ? "bg-amber-100 text-amber-700"
                      : iv.status === "accepted" ? "bg-green-100 text-green-700"
                      : "bg-neutral-100 text-neutral-600")}>
                      {iv.status}
                    </span>
                    {iv.status === "pending" && (
                      <button onClick={() => revokeInvite(iv.invite_id)} disabled={busy}
                        data-testid={`team-invite-revoke-${iv.invite_id}`}
                        title="Revoke invite"
                        className="rounded-lg border border-neutral-200 bg-white p-1.5 text-neutral-500 hover:text-red-600">
                        <Ban className="h-3.5 w-3.5"/>
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Members */}
      <div className="mt-6 rounded-2xl border border-neutral-200 bg-white p-6" data-testid="team-members-card">
        <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">Members ({team.length})</p>
        <ul className="mt-4 divide-y divide-neutral-100">
          {team.map(m => (
            <li key={m.user_id} className="flex flex-wrap items-center justify-between gap-3 py-3"
                data-testid={`team-member-${m.user_id}`}>
              <div className="min-w-0 flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#0A8B85]/10 text-sm font-bold text-[#0A8B85]">
                  {(m.name || "?").charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-neutral-900 truncate">{m.name || m.email}</p>
                    {m.is_primary && <Crown className="h-3.5 w-3.5 text-amber-500" title="Primary founder"/>}
                    {m.is_me && <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-bold text-neutral-600">You</span>}
                  </div>
                  <p className="text-xs text-neutral-500">{m.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {iAmPrimary && !m.is_primary ? (
                  <>
                    <select value={m.role} onChange={e => changeRole(m.user_id, e.target.value)}
                      data-testid={`team-role-select-${m.user_id}`}
                      className="rounded-lg border border-neutral-200 bg-white px-2 py-1 text-xs font-semibold text-neutral-700 outline-none focus:border-[#0A8B85]">
                      {BUSINESS_ROLES.map(r => <option key={r}>{r}</option>)}
                    </select>
                    <button onClick={() => removeMember(m.user_id)} data-testid={`team-remove-${m.user_id}`}
                      className="rounded-lg border border-red-200 bg-red-50 p-1.5 text-red-600 hover:bg-red-100">
                      <Trash2 className="h-3.5 w-3.5"/>
                    </button>
                  </>
                ) : (
                  <span className="rounded-full bg-neutral-100 px-3 py-1 text-xs font-semibold text-neutral-700">{m.role}</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Pending requests (primary only) */}
      {iAmPrimary && (
        <div className="mt-6 rounded-2xl border border-neutral-200 bg-white p-6" data-testid="team-pending-card">
          <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">Pending join requests ({requests.length})</p>
          {requests.length === 0 ? (
            <p className="mt-3 text-sm text-neutral-500">No pending requests. When teammates request to join, you'll see them here.</p>
          ) : (
            <ul className="mt-4 space-y-3">
              {requests.map(r => (
                <li key={r.request_id} className="rounded-xl border border-amber-200 bg-amber-50 p-4"
                    data-testid={`team-request-${r.request_id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-neutral-900">{r.user_name}
                        <span className="ml-2 rounded-full bg-amber-700 px-2 py-0.5 text-[10px] font-bold uppercase text-white">{r.business_role}</span>
                      </p>
                      <p className="text-xs text-neutral-600">{r.user_email}</p>
                      {r.message && (
                        <div className="mt-2 flex items-start gap-1.5 rounded-lg bg-white px-3 py-2 text-xs text-neutral-700">
                          <MessageSquare className="mt-0.5 h-3 w-3 text-neutral-400"/>
                          <span>{r.message}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => approve(r.request_id)} disabled={busy}
                        data-testid={`team-approve-${r.request_id}`}
                        className="inline-flex items-center gap-1 rounded-lg bg-[#0A8B85] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#066667] disabled:opacity-50">
                        <Check className="h-3 w-3"/> Approve
                      </button>
                      <button onClick={() => reject(r.request_id)} disabled={busy}
                        data-testid={`team-reject-${r.request_id}`}
                        className="inline-flex items-center gap-1 rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-50">
                        <X className="h-3 w-3"/> Reject
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* My outgoing requests */}
      {outgoing.length > 0 && (
        <div className="mt-6 rounded-2xl border border-neutral-200 bg-white p-6" data-testid="team-outgoing-card">
          <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">My requests</p>
          <ul className="mt-3 space-y-2">
            {outgoing.map(r => (
              <li key={r.request_id} className="flex items-center justify-between gap-3 text-sm">
                <div>
                  <p className="font-semibold text-neutral-900">{r.company_name}</p>
                  <p className="text-xs text-neutral-500">As {r.business_role} · sent {new Date(r.created_at).toLocaleDateString()}</p>
                </div>
                <span className={"rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase " +
                  (r.status === "pending" ? "bg-amber-100 text-amber-700"
                  : r.status === "approved" ? "bg-green-100 text-green-700"
                  : "bg-neutral-100 text-neutral-600")}>
                  {r.status}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </AppShell>
  );
}
